/**
 * AdminVehicleDuplicatesPage — Fahrzeug-Doubletten prüfen & zusammenführen.
 * Route: /admin/vehicle-duplicates (admin only)
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Car, ArrowLeft, RefreshCw, Merge, Wand2, Check } from "lucide-react";
import { toast } from "sonner";

import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { TestVehicleCleanupCard } from "../components/admin/TestVehicleCleanupCard";
import { VinlessCleanupCard } from "../components/admin/VinlessCleanupCard";
import { TestAccountCleanupCard } from "../components/admin/TestAccountCleanupCard";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; }
};

export default function AdminVehicleDuplicatesPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [keepers, setKeepers] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/vehicle-duplicates");
            setData(r.data);
            const k = {};
            (r.data.groups || []).forEach((g) => { k[g.vin] = g.entries[0]?.id; });
            setKeepers(k);
        } catch { setData(null); }
        setLoading(false);
    }, []);

    useEffect(() => {
        if (user === false) navigate("/login");
        if (user && user.role !== "admin") navigate("/");
        if (user) load();
    }, [user, navigate, load]);

    const mergeGroup = async (g) => {
        const keep = keepers[g.vin];
        const remove = g.entries.filter((e) => e.id !== keep).map((e) => e.id);
        if (!keep || !remove.length) return;
        setBusy(true);
        try {
            const r = await api.post("/admin/vehicle-duplicates/merge", { keep_id: keep, remove_ids: remove });
            toast.success(`FIN ${g.vin}: ${r.data.removed} Doublette(n) entfernt`);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Merge fehlgeschlagen"); }
        setBusy(false);
    };

    const autoMerge = async () => {
        setBusy(true);
        try {
            const dry = await api.post("/admin/vehicle-duplicates/auto-merge", { dry_run: true });
            const n = dry.data.mergeable_groups;
            if (!n) { toast.info("Keine sicher zusammenführbaren Doubletten (gleiche Person) gefunden."); setBusy(false); return; }
            if (!window.confirm(`${n} Doubletten-Gruppen stammen sicher von derselben Person (gleiche Kundennr./E-Mail/Telefon).\n${dry.data.removable_records} überflüssige Datensätze werden entfernt (ältester bleibt jeweils erhalten).\n\nJetzt automatisch zusammenführen?`)) { setBusy(false); return; }
            const r = await api.post("/admin/vehicle-duplicates/auto-merge", { dry_run: false });
            toast.success(`${r.data.merged_groups} Gruppen zusammengeführt, ${r.data.removed_records} Doubletten entfernt`);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Auto-Merge fehlgeschlagen"); }
        setBusy(false);
    };

    if (!user) return <div className="p-8">Lade…</div>;

    return (
        <div className="min-h-screen bg-gray-50" data-testid="vehicle-duplicates-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
                    <Link to="/admin" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="back-to-admin">
                        <ArrowLeft className="w-3 h-3" /> Admin-Dashboard
                    </Link>
                    <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                            <Car className="w-7 h-7 text-[#FFCC00]" />
                            <div>
                                <div className="text-xs uppercase tracking-wider text-gray-400">CRM · Datenqualität</div>
                                <h1 className="font-display font-black text-4xl md:text-5xl">Fahrzeug-Doubletten</h1>
                                <p className="text-sm text-gray-300 mt-1" data-testid="dup-summary">
                                    {data ? `${data.total_groups} FIN(s) mehrfach vorhanden · ${data.total_records} betroffene Datensätze` : "…"}
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={autoMerge} disabled={busy || !data?.total_groups} className="bg-[#FFCC00] text-[#111827] hover:bg-yellow-400 rounded-sm font-bold" data-testid="auto-merge-btn">
                                <Wand2 className="w-4 h-4 mr-1" /> Sichere Doubletten automatisch bereinigen
                            </Button>
                            <Button onClick={load} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800 rounded-sm" data-testid="refresh-btn">
                                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Neu prüfen
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 space-y-4">
                {/* Jul-2026 (User-Wunsch) — Test-/Demo-Fahrzeuge bereinigen */}
                <TestVehicleCleanupCard />
                <VinlessCleanupCard />
                <TestAccountCleanupCard />
                {!loading && data?.total_groups === 0 && (
                    <div className="bg-green-50 border border-green-300 rounded-sm p-6 flex items-center gap-3" data-testid="no-duplicates">
                        <Check className="w-6 h-6 text-green-600" />
                        <div className="font-display font-black text-xl text-green-900">Keine Fahrzeug-Doubletten gefunden — alle FINs sind eindeutig.</div>
                    </div>
                )}
                {(data?.groups || []).map((g) => (
                    <div key={g.vin} className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid={`dup-group-${g.vin}`}>
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
                            <div className="text-sm">
                                <span className="font-mono-num font-bold">{g.vin}</span>
                                <span className="ml-2 text-xs text-gray-500">{g.count}× vorhanden</span>
                                {g.same_person && <span className="ml-2 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900">gleiche Person</span>}
                            </div>
                            <Button size="sm" onClick={() => mergeGroup(g)} disabled={busy} className="bg-[#111827] text-white hover:bg-gray-700 rounded-sm" data-testid={`merge-btn-${g.vin}`}>
                                <Merge className="w-3.5 h-3.5 mr-1" /> Zusammenführen
                            </Button>
                        </div>
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                <tr>
                                    <th className="px-3 py-1.5 text-left">Behalten</th>
                                    <th className="px-3 py-1.5 text-left">Kunde</th>
                                    <th className="px-3 py-1.5 text-left">Fahrzeug</th>
                                    <th className="px-3 py-1.5 text-left">Kennzeichen</th>
                                    <th className="px-3 py-1.5 text-left">Händler/Standort</th>
                                    <th className="px-3 py-1.5 text-left">Quelle</th>
                                    <th className="px-3 py-1.5 text-left">Angelegt</th>
                                </tr>
                            </thead>
                            <tbody>
                                {g.entries.map((e) => (
                                    <tr key={e.id} className={`border-t border-gray-100 ${keepers[g.vin] === e.id ? "bg-green-50/60" : ""}`}>
                                        <td className="px-3 py-2">
                                            <input type="radio" name={`keep-${g.vin}`} checked={keepers[g.vin] === e.id}
                                                onChange={() => setKeepers((k) => ({ ...k, [g.vin]: e.id }))}
                                                data-testid={`keep-radio-${e.id}`} />
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="font-medium">{e.customer_name}</div>
                                            <div className="text-[11px] text-gray-500">{e.customer_email || e.customer_phone || "—"}</div>
                                        </td>
                                        <td className="px-3 py-2">{e.brand} {e.model}</td>
                                        <td className="px-3 py-2 font-mono-num text-xs">{e.license_plate || "—"}</td>
                                        <td className="px-3 py-2 text-xs">{e.dealer_name}</td>
                                        <td className="px-3 py-2 text-[11px] text-gray-500">{e.source || "—"}</td>
                                        <td className="px-3 py-2 text-[11px] font-mono-num text-gray-500">{fmtDate(e.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))}
                {data && data.total_groups > (data.groups || []).length && (
                    <div className="text-xs text-gray-500 text-center" data-testid="dup-more-hint">
                        Es werden die ersten {(data.groups || []).length} von {data.total_groups} Gruppen angezeigt — nach dem Bereinigen „Neu prüfen" klicken.
                    </div>
                )}
            </div>
        </div>
    );
}
